import { useMemo } from "react";
import type { DemoAttendeeRow, LeadCalRow } from "./sql";
import { uniqueDemos, uniqueAttendees } from "./helpers";
import type { ViewMode } from "./helpers";
import { KpiCard } from "../../lib/funnel/charts";
import { fmtN, fmtPct } from "../../lib/funnel/helpers";

type Props = {
  demoRows: DemoAttendeeRow[];
  // Cohort+country only (no role/toggle). Used for "Demos run" so the headline
  // matches the spine even when role/toggle would drop entire demos.
  cohortDemoRows: DemoAttendeeRow[];
  leadCalRows: LeadCalRow[];
  viewMode: ViewMode;
};

// All five cards use distinct attendees as the canonical base. Previously the
// rate cards used attendee rows (348) while "Distinct attendees" said 335;
// drilldown column lists used a third number. Unifying on distinct removes
// the multi-attendee-demo double counting and matches the column lists.
export function KpiRow({ demoRows, cohortDemoRows, leadCalRows, viewMode }: Props) {
  const stats = useMemo(() => {
    const totalDemos = uniqueDemos(cohortDemoRows).length;

    if (viewMode === "company") {
      // demoRows are already collapsed to one row per company / unmatched demo.
      const totalUnits = demoRows.length;
      let signedUp = 0;
      let activated = 0;
      let paid = 0;
      for (const r of demoRows) {
        if (r.signup_timing_bucket !== "never") signedUp++;
        if (r.activated) activated++;
        if (r.on_paid_plan === true) paid++;
      }
      return { totalDemos, totalUnits, signedUp, activated, paid };
    }

    // Attendee mode: collapse per email so multi-demo attendees count once.
    const totalUnits = uniqueAttendees(demoRows).size;
    const byAttendee = new Map<string, { signedUp: boolean; activated: boolean; paid: boolean }>();
    for (const r of demoRows) {
      const cur = byAttendee.get(r.attendee_email) ?? { signedUp: false, activated: false, paid: false };
      if (r.signup_timing_bucket !== "never") cur.signedUp = true;
      if (r.activated) cur.activated = true;
      if (r.user_external_id != null && r.on_paid_plan === true) cur.paid = true;
      byAttendee.set(r.attendee_email, cur);
    }
    let signedUp = 0, activated = 0, paid = 0;
    for (const v of byAttendee.values()) {
      if (v.signedUp) signedUp++;
      if (v.activated) activated++;
      if (v.paid) paid++;
    }
    return { totalDemos, totalUnits, signedUp, activated, paid };
  }, [demoRows, cohortDemoRows, viewMode]);

  void leadCalRows;

  const isCompany = viewMode === "company";
  const unitLabel = isCompany ? "Distinct companies" : "Distinct attendees";
  const unitInfo = isCompany
    ? "Distinct prospect companies in cohort. A prospect = matched company_external_id when an attendee has signed up, otherwise the demo itself (so an unmatched company that demoed twice counts as 2 prospects until any attendee signs up)."
    : "Distinct attendee emails across the demos in cohort. Excludes @gomarloo.com.";
  const signedUpLabel = isCompany ? "Company → signed up" : "Attendee → signed up";
  const signedUpInfo = isCompany
    ? "Share of prospect companies where at least one attendee matches a public.users record (signed up at any point: before, during, or after a demo with that company)."
    : "Share of distinct attendees that match a public.users record (signed up at any point: before, during, or after the demo).";
  const signedUpSubUnit = isCompany ? "" : "";
  const paidFromSignedLabel = "Signed up → paid";
  const paidFromSignedInfo = isCompany
    ? "Of signed-up companies, share whose workspace is on a Plus or Pro plan (lifetime). Includes companies that were already paid before they demoed."
    : "Lifetime paid status across all signed-up attendees. Includes joiners of an already-paid workspace.";
  const endToEndLabel = "Demo → paid";
  const endToEndInfo = isCompany
    ? "End-to-end conversion of prospect companies to paid (Plus or Pro)."
    : "End-to-end conversion of distinct demo attendees to paid.";

  return (
    <div className="kpi-grid">
      <KpiCard
        label="Demos run"
        value={fmtN(stats.totalDemos)}
        info="Count of distinct demos in cohort (cohort + country + demo type filters; role/support toggle do not affect this number). Source: reporting.demo where follow_up_context.is_demo = true."
      />
      <KpiCard
        label={unitLabel}
        value={fmtN(stats.totalUnits)}
        info={unitInfo}
      />
      <KpiCard
        label={signedUpLabel}
        value={fmtPct(stats.signedUp, stats.totalUnits)}
        sub={`${fmtN(stats.signedUp)} / ${fmtN(stats.totalUnits)}${signedUpSubUnit}`}
        tone="primary"
        info={signedUpInfo}
      />
      <KpiCard
        label={paidFromSignedLabel}
        value={fmtPct(stats.paid, stats.signedUp)}
        sub={`${fmtN(stats.paid)} / ${fmtN(stats.signedUp)} ${isCompany ? "signed-up companies" : "signed-up attendees"}`}
        tone="primary"
        info={paidFromSignedInfo}
      />
      <KpiCard
        label={endToEndLabel}
        value={fmtPct(stats.paid, stats.totalUnits)}
        sub={`${fmtN(stats.paid)} / ${fmtN(stats.totalUnits)}`}
        tone="primary"
        info={endToEndInfo}
      />
    </div>
  );
}
